import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants'

const SELECTORS = {
  videoPlayer: '.js-video-player',
  videoPlayerOverlay: '.js-video-player-overlay',
  videoIframe: '.js-video-player-iframe',
  videoInfoContainer: '.js-video-player-info-container',
  videoPlayButton: '.js-video-player__overlay__media-button'
}

/**
 * Manages YouTube video embed behaviors.
 */
class VideoPlayer {
  windowIsBlurred = false

  /**
   * Formats time given in seconds into X:XX:XX format.
   * @param {Number} duration - Length of the video in seconds
   * @return {Object} - Object containing human readable and aria-formatted time strings.
   */
  static formatTime(duration) {
    const hrs = `${~~(duration / 3600)}`
    const mins = `${~~((duration % 3600) / 60)}`
    const secs = `${~~duration % 60}`

    // Output like "1:01" or "4:03:59" or "123:03:59"
    let formatted = ''
    let aria = ''

    if (hrs > 0) {
      formatted += `${hrs.padStart(2, '0')}:`
      aria += `${hrs} hours `
    }

    formatted += `${mins.padStart(2, '0')}:${secs.padStart(2, '0')}`
    aria += `${mins} minutes and ${secs} seconds`

    return {
      formattedDuration: formatted,
      ariaDuration: aria
    }
  }

  /**
   * Sets up the class by creating variables and calling init().
   * @param {Element} element - The top level HTML element that triggered instantiation
   */
  constructor (element) {
    this.element = element
    this.iframe = this.element.querySelector(SELECTORS.videoIframe)
    this.overlay = this.element.querySelector(SELECTORS.videoPlayerOverlay)
    this.videoInfoContainer = this.element.querySelector(SELECTORS.videoInfoContainer)
    this.videoPlayBtn = this.element.querySelector(SELECTORS.videoPlayButton)
    this.mediaButton = this.element.querySelector(`${SELECTORS.videoPlayButton} button`)
    this.query = window.matchMedia('(min-width: 768px)')

    this.init()
  }

  /**
   * Initializes the video player.
   */
  init() {
    // Set button hover states.
    this.element.addEventListener('mouseenter', () => { this.mediaButton.classList.add('--hover') })
    this.element.addEventListener('mouseleave', () => { this.mediaButton.classList.remove('--hover') })

    // Set overlay events.
    this.overlay.addEventListener('click', () => { this.setPlayingState() })

    // Listen for when our main window loses focus and refocus it when the mouse
    // re-enters.
    window.addEventListener('blur', () => { this.handleWindowBlur() })
    window.onmouseover = () => {
      if (this.windowIsBlurred) {
        window.focus()
        this.windowIsBlurred = false
      }
    }

    window.addEventListener('message', (event) => {
      if (this.iframe.contentWindow === event.source) {
        // const data = JSON.parse(event.data)

        // If data.info.playerState exists, the value maps like this...
        // -2 -> not ready
        // -1 -> unstarted
        //  0 -> ended
        //  1 -> playing
        //  2 -> paused
        //  3 -> buffering
        //  5 -> video cued
      }
    })

    this.iframe.contentWindow.postMessage(JSON.stringify({
      event: 'listening'
    }), '*')

    if (!('youtubeplayers' in window.unlocked)) {
      window.unlocked.youtubeplayers = {}
    }

    window.unlocked.youtubeplayers[this.element.id] = {
      play: () => {
        this.play()
      },
      pause: () => {
        this.pause()
      },
      stop: () => {
        this.stop()
      }
    }
  }

  /**
   * Handle the window blur event.
   */
  handleWindowBlur = () => {
    this.windowIsBlurred = true

    // Did our iframe gain focus? If so, switch to the playing state.
    if (document.activeElement === this.iframe) {
      this.setPlayingState()
    }
  }

  setPlayingState = () => {
    this.element.classList.add('-playing')
    this.overlay.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
    this.overlay.ariaHidden = 'true'
    this.iframe.tabIndex = 0
    this.iframe.ariaHidden = 'false'
  }

  unsetPlayingState = () => {
    this.element.classList.remove('-playing')
    this.overlay.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    this.overlay.ariaHidden = 'false'
    this.iframe.tabIndex = -1
    this.iframe.ariaHidden = 'true'
  }

  // These methods send messages directly to the YouTube iFrame, allowing
  // programmatic interaction *without* using the YouTube JS API :)

  play = () => {
    this.setPlayingState()

    this.iframe.contentWindow.postMessage(JSON.stringify({
      event: 'command',
      func: 'playVideo'
    }), '*')
  }

  pause = () => {
    this.iframe.contentWindow.postMessage(JSON.stringify({
      event: 'command',
      func: 'pauseVideo'
    }), '*')
  }

  stop = () => {
    this.unsetPlayingState()

    this.iframe.contentWindow.postMessage(JSON.stringify({
      event: 'command',
      func: 'stopVideo'
    }), '*')
  }
}

export default {
  selector: SELECTORS.videoPlayer,
  Source: VideoPlayer
}
